import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@bb-ui/react-library/dist/components/Tooltip';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';

import { useStyles } from './FeedbackWidget.styles';
import { RatingFace } from './RatingFace';
import { MAX_RATING, MIN_RATING } from './FeedbackWidget.types';

export interface IRatingScaleProps {
    value?: number;
    onChange: (rating: number) => void;
    disabled?: boolean;
    /** Id of the element labelling the scale, used as the radiogroup's label. */
    labelledBy: string;
}

const LEGEND_ID = 'feedback-rating-legend';

const RATINGS = Array.from(
    { length: MAX_RATING - MIN_RATING + 1 },
    (_, index) => MIN_RATING + index,
);

/**
 * The 1-5 rating as five faces, built as a radio group so it reads as one
 * choice with arrow-key navigation.
 */
export const RatingScale: React.FunctionComponent<IRatingScaleProps> = ({
    value,
    onChange,
    disabled,
    labelledBy,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    // Roving tabindex: the group holds a single tab stop, and arrows move within it.
    const focusedRating = value ?? MIN_RATING;

    const handleKeyDown = (event: React.KeyboardEvent, rating: number) => {
        const offset = {
            ArrowRight: 1,
            ArrowDown: 1,
            ArrowLeft: -1,
            ArrowUp: -1,
        }[event.key];

        let next: number | undefined;
        if (offset) {
            // Wraps, which is what the radio group pattern calls for.
            next =
                ((rating - MIN_RATING + offset + RATINGS.length) %
                    RATINGS.length) +
                MIN_RATING;
        } else if (event.key === 'Home') {
            next = MIN_RATING;
        } else if (event.key === 'End') {
            next = MAX_RATING;
        }

        if (next === undefined) return;

        event.preventDefault();
        onChange(next);
        document.getElementById(`feedback-rating-${next}`)?.focus();
    };

    return (
        <div className={classes.ratingSection}>
            <div
                role="radiogroup"
                aria-labelledby={labelledBy}
                // Says which end of the scale is good.
                aria-describedby={LEGEND_ID}
                className={classes.ratingGroup}
                data-testid="feedback-rating-group"
            >
                {RATINGS.map(rating => {
                    const selected = value === rating;
                    const name = t(`feedback.form.ratingNames.${rating}`);
                    return (
                        // Names the face; on a wrapper so it is not read twice.
                        <Tooltip key={rating} title={name} placement="top">
                            <span className={classes.ratingCell}>
                                <button
                                    type="button"
                                    id={`feedback-rating-${rating}`}
                                    role="radio"
                                    aria-checked={selected}
                                    // Name plus place on the scale.
                                    aria-label={t(
                                        'feedback.form.ratingOption',
                                        {
                                            label: name,
                                            rating,
                                            max: MAX_RATING,
                                        },
                                    )}
                                    tabIndex={focusedRating === rating ? 0 : -1}
                                    disabled={disabled}
                                    className={`${classes.ratingButton} ${
                                        selected
                                            ? classes.ratingButtonSelected
                                            : ''
                                    }`}
                                    onClick={() => onChange(rating)}
                                    onKeyDown={event =>
                                        handleKeyDown(event, rating)
                                    }
                                    data-testid={`feedback-rating-${rating}`}
                                >
                                    <RatingFace
                                        rating={rating}
                                        className={classes.ratingFace}
                                    />
                                </button>
                            </span>
                        </Tooltip>
                    );
                })}
            </div>
            <div className={classes.ratingLegend} id={LEGEND_ID}>
                <Typography variant="caption" component="span">
                    {t('feedback.form.ratingLow')}
                </Typography>
                <Typography variant="caption" component="span">
                    {t('feedback.form.ratingHigh')}
                </Typography>
            </div>
        </div>
    );
};
